import { CommonModule } from '@angular/common';
import { Component, Injector, OnInit, OnDestroy } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { RegistrationDto, VenViewModel } from '../../../../model/vens.model';
import { goBack } from '../../../../common/common';
import {ApiService, CustomHttpParams} from 'src/app/services/api.service';
import { Subject, takeUntil } from 'rxjs';
import {HttpParams} from "@angular/common/http";
import {LoaderComponent} from "../../../common/loader/loader.component";

@Component({
  selector: "app-create-registration-index",
  standalone: true,
  imports: [RouterModule, CommonModule, FormsModule, ReactiveFormsModule, LoaderComponent],
  templateUrl: "./create-registration.component.html",
  styleUrl: "./create-registration.component.scss",
})
export class CreateRegistrationComponent implements OnInit, OnDestroy {
  // Form
  registrationForm = new FormGroup({
    venId: new FormControl<any>(null, Validators.required),
    pollRate: new FormControl<string>(''),
    mode: new FormControl<string>(''),
    replyLimit: new FormControl<number | null>(null),
    reportOnly: new FormControl<string>('false')
  });
  protected isLoading: boolean = false;
  protected unsubscribe = new Subject<void>();
  public editing: boolean = false;
  public error: string | null = null;
  public registrationId: number | null = null;
  public router: Router;

  public vens: VenViewModel[] = [];
  public selectedVen: VenViewModel | undefined;

  protected registrationDto: RegistrationDto = {
    registration_id: 0,
    reportOnly: false,
    reply_limit: 0,
    poll_rate: "",
    created_at: "",
    httpPull: false,
    profile_name: "",
    transport: "",
    updated_by: "",
    ven: {
      ven_id: '',
      id: 0
    },
    vtns: [],
    xmlSignature: "",
    mode: "poll",
  };

  constructor(
    private injector: Injector,
    private registrationService: ApiService
  ) {
    this.router = injector.get(Router);
    this.router.events.subscribe((event: any) => {
      if (event?.snapshot?.params.id) {
        this.editing = true;
        this.registrationId = event.snapshot.params.id;
      }
    });
  }

  ngOnInit(): void {
    const queryParams: CustomHttpParams = {
      'depths': JSON.stringify({
        'vtn': 1,
        'cert': 1,
        'registration': 1,
      })
    };
    this.isLoading = true;
    this.registrationService.findVens(queryParams).subscribe({
      next: (response) => {
        this.vens = response.filter((ven: any) => {
          return ven.cert && ven.vtn && !ven.registration;
        });
        if (this.vens.length === 0) {
          this.error = 'No suitable Ven found: must have a cert and VTN associated and none registrations yet.'
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.error = err;
        this.isLoading = false;
      }
    });

    // If editing, load the registration-index data
    if (this.editing && this.registrationId) {
      const queryParams: CustomHttpParams = {
        'depth': 1,
        'registration_id': this.registrationId,
      };
      this.registrationService.findRegistration(queryParams).subscribe({
        next: (response: any) => {
          if (response && response.length) {
            // Find the registration-index with the matching ID
            const foundRegistration = response.find((reg: any) => reg.registration_id === this.registrationId);
            if (foundRegistration) {
              this.registrationDto = foundRegistration;
              this.registrationForm.controls.venId.setValue({
                id: this.registrationDto.ven.id,
                profile: this.registrationDto.profile_name
              });
              this.registrationForm.controls.mode.setValue(this.registrationDto.mode);
              this.registrationForm.controls.pollRate.setValue(this.registrationDto.poll_rate);
              this.registrationForm.controls.replyLimit.setValue(this.registrationDto.reply_limit);
              this.registrationForm.controls.reportOnly.setValue(
                this.registrationDto.reportOnly ? 'true' : 'false'
              );
              this.registrationForm.disable();
            }
          }
        },
        error: (err) => {
          console.error(err);
          this.error = err;
        }
      });
    }
    this.setupFormSubscriptions();
  }

  setupFormSubscriptions() {
    this.registrationForm.controls.venId.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(val => {
        if (val) {
          this.selectedVen = val;
          if (val.profile) {
            this.registrationDto.profile_name = val.profile;
          }
        }
      });

    this.registrationForm.controls.pollRate.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(val => {
        if (val) this.registrationDto.poll_rate = val;
      });

    this.registrationForm.controls.replyLimit.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(val => {
        if (val !== null) this.registrationDto.reply_limit = val;
      });

    this.registrationForm.controls.reportOnly.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(val => {
        this.registrationDto.reportOnly = val === 'true';
      });
  }

  save() {
    if (this.registrationForm.invalid) {
      this.error = 'Please fill out all required fields correctly.';
      return;
    }

    // Prepare registration-index data for submission
    const registrationData: any = {
      ven: this.selectedVen,
      mode: this.selectedVen?.mode || 'poll',
    };

    if (this.selectedVen?.profile === '2.0a') {
      registrationData.poll_rate = this.registrationDto.poll_rate;
      registrationData.reply_limit = this.registrationDto.reply_limit;
    } else if (this.selectedVen?.profile === '2.0b') {
      registrationData.reportOnly = this.registrationDto.reportOnly;
    }

    this.registrationService.createRegistration(registrationData).subscribe({
      next: (response: any) => {
        if (response?.success === true) {
          goBack('/registrations', this.router);
        } else {
          this.error = 'Registration creation process failed.';
        }
      },
      error: (err) => {
        console.error(err);
        this.error = err;
      }
    });
  }

  backPage(url: string, router: Router) {
    goBack(url, router);
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
